export const HUNTING_SYSTEM_VERSION = 1;

const STRIKE_COOLDOWN_SECONDS = 0.35;
const EAT_COOLDOWN_SECONDS = 0.25;
const STRIKE_MIN_DOT = Math.cos(Math.PI / 3);

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeHuntingChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function normalizeTick(value) {
  return value === null ? null : assertNonNegativeInteger(value, 'tick');
}

function distanceSquared(left, right) {
  return ((left.x - right.x) ** 2)
    + ((left.y - right.y) ** 2)
    + ((left.z - right.z) ** 2);
}

function strikeRange(snake) {
  return 0.7 + (snake.length * 0.5);
}

function eatRange(snake) {
  return 0.55 + (snake.length * 0.4);
}

function strikeDamage(snake) {
  return 10 + (snake.length * 10) + (snake.mass * 20);
}

function isFacing(snake, target) {
  const dx = target.position.x - snake.position.x;
  const dz = target.position.z - snake.position.z;
  const magnitude = Math.hypot(dx, dz);
  if (magnitude === 0) return true;
  const directionX = Math.sin(snake.heading);
  const directionZ = Math.cos(snake.heading);
  return ((directionX * dx) + (directionZ * dz)) / magnitude >= STRIKE_MIN_DOT;
}

function normalizeCounters(counters = {}) {
  assertPlainObject(counters, 'counters');
  return {
    strikes: assertNonNegativeInteger(counters.strikes ?? 0, 'counters.strikes'),
    hits: assertNonNegativeInteger(counters.hits ?? 0, 'counters.hits'),
    kills: assertNonNegativeInteger(counters.kills ?? 0, 'counters.kills'),
    eats: assertNonNegativeInteger(counters.eats ?? 0, 'counters.eats'),
    misses: assertNonNegativeInteger(counters.misses ?? 0, 'counters.misses'),
    cooldowns: assertNonNegativeInteger(counters.cooldowns ?? 0, 'counters.cooldowns'),
  };
}

function buildEvent({
  ok,
  type,
  requestId,
  outcome,
  reason = null,
  tick,
  simulationTime,
  snakeEntityId = null,
  targetEntityId = null,
  damage = 0,
  targetHealth = null,
  targetLifecycle = null,
  rewards = null,
  snakeAfter = null,
}) {
  return deepFreeze({
    ok: Boolean(ok),
    duplicate: false,
    type: assertNonEmptyString(type, 'type'),
    requestId: assertNonEmptyString(requestId, 'requestId'),
    outcome: assertNonEmptyString(outcome, 'outcome'),
    reason: reason === null ? null : assertNonEmptyString(reason, 'reason'),
    tick: normalizeTick(tick),
    simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
    snakeEntityId: snakeEntityId === null ? null : assertNonEmptyString(snakeEntityId, 'snakeEntityId'),
    targetEntityId: targetEntityId === null ? null : assertNonEmptyString(targetEntityId, 'targetEntityId'),
    damage: assertFiniteNonNegative(damage, 'damage'),
    targetHealth: targetHealth === null ? null : assertFiniteNonNegative(targetHealth, 'targetHealth'),
    targetLifecycle: targetLifecycle === null
      ? null
      : assertNonEmptyString(targetLifecycle, 'targetLifecycle'),
    rewards: rewards === null ? null : deepFreeze(clone(rewards)),
    snakeAfter: snakeAfter === null ? null : deepFreeze(clone(snakeAfter)),
  });
}

function normalizeEvent(event) {
  assertPlainObject(event, 'event');
  if (event.duplicate !== false) {
    throw new RangeError('stored hunting events must not be duplicate responses');
  }
  const normalized = buildEvent(event);
  if (JSON.stringify(canonicalize(normalized)) !== JSON.stringify(canonicalize(event))) {
    throw new RangeError('stored hunting event is not canonical');
  }
  return normalized;
}

function duplicateEvent(event) {
  return deepFreeze({ ...clone(event), duplicate: true });
}

function deriveCounters(events) {
  const counters = normalizeCounters();
  for (const event of events) {
    if (event.type === 'strike') counters.strikes += 1;
    if (event.outcome === 'hit' || event.outcome === 'kill') counters.hits += 1;
    if (event.outcome === 'kill') counters.kills += 1;
    if (event.outcome === 'consumed') counters.eats += 1;
    if (event.outcome === 'miss') counters.misses += 1;
    if (event.outcome === 'cooldown') counters.cooldowns += 1;
  }
  return counters;
}

export class HuntingSystem {
  constructor({ entityWorld, snakeState, wildlifeSystem }) {
    assertMethod(entityWorld, 'get', 'entityWorld');
    assertMethod(entityWorld, 'snapshot', 'entityWorld');
    assertMethod(entityWorld, 'restore', 'entityWorld');
    assertMethod(snakeState, 'snapshot', 'snakeState');
    assertMethod(snakeState, 'restore', 'snakeState');
    assertMethod(snakeState, 'feed', 'snakeState');
    assertMethod(snakeState, 'grow', 'snakeState');
    assertMethod(wildlifeSystem, 'get', 'wildlifeSystem');
    assertMethod(wildlifeSystem, 'list', 'wildlifeSystem');
    assertMethod(wildlifeSystem, 'snapshot', 'wildlifeSystem');
    assertMethod(wildlifeSystem, 'restore', 'wildlifeSystem');
    assertMethod(wildlifeSystem, 'damage', 'wildlifeSystem');
    assertMethod(wildlifeSystem, 'consume', 'wildlifeSystem');
    assertMethod(wildlifeSystem, 'getSpeciesDefinition', 'wildlifeSystem');
    this.entityWorld = entityWorld;
    this.snakeState = snakeState;
    this.wildlifeSystem = wildlifeSystem;
    this.revision = 0;
    this.nextRequestNumber = 1;
    this.nextStrikeTime = 0;
    this.nextEatTime = 0;
    this.processedRequests = new Map();
    this.lastEvent = null;
    this.counters = normalizeCounters();
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
  }

  #prepareRequest(value) {
    if (value !== undefined && value !== null) {
      return {
        requestId: assertNonEmptyString(value, 'requestId'),
        nextRequestNumber: null,
      };
    }

    let candidateNumber = this.nextRequestNumber;
    let requestId;
    do {
      requestId = `request-${String(candidateNumber).padStart(6, '0')}`;
      candidateNumber += 1;
    } while (this.processedRequests.has(requestId));

    return { requestId, nextRequestNumber: candidateNumber };
  }

  #record(event, counterChanges = {}, nextRequestNumber = null) {
    const nextCounters = normalizeCounters({
      ...this.counters,
      ...Object.fromEntries(
        Object.entries(counterChanges).map(([key, delta]) => [key, this.counters[key] + delta]),
      ),
    });
    this.processedRequests.set(event.requestId, event);
    this.lastEvent = event;
    this.revision += 1;
    if (nextRequestNumber !== null) this.nextRequestNumber = nextRequestNumber;
    this.counters = nextCounters;
    return event;
  }

  #snake() {
    const snake = this.snakeState.snapshot();
    return snake && snake.lifecycle === 'alive' ? snake : null;
  }

  #assertEntityType(id, expectedType) {
    const entity = this.entityWorld.get(id);
    if (!entity) throw new Error(`Entity is missing: ${id}`);
    if (entity.type !== expectedType) {
      throw new RangeError(`Entity ${id} must have type ${expectedType}`);
    }
    return entity;
  }

  #selectStrikeTarget(snake, explicitId = undefined) {
    const candidates = explicitId === undefined
      ? this.wildlifeSystem.list({ lifecycle: 'alive' })
      : [this.wildlifeSystem.get(explicitId)].filter(Boolean);
    const rangeSquared = strikeRange(snake) ** 2;
    return candidates
      .filter((record) => record.lifecycle === 'alive')
      .filter((record) => distanceSquared(record.position, snake.position) <= rangeSquared)
      .filter((record) => isFacing(snake, record))
      .sort((left, right) => {
        const distanceDifference = distanceSquared(left.position, snake.position)
          - distanceSquared(right.position, snake.position);
        return distanceDifference || left.entityId.localeCompare(right.entityId);
      })[0] ?? null;
  }

  #selectEatTarget(snake, explicitId = undefined) {
    const candidates = explicitId === undefined
      ? this.wildlifeSystem.list({ lifecycle: 'corpse' })
      : [this.wildlifeSystem.get(explicitId)].filter(Boolean);
    const rangeSquared = eatRange(snake) ** 2;
    return candidates
      .filter((record) => record.lifecycle === 'corpse')
      .filter((record) => distanceSquared(record.position, snake.position) <= rangeSquared)
      .sort((left, right) => {
        const distanceDifference = distanceSquared(left.position, snake.position)
          - distanceSquared(right.position, snake.position);
        return distanceDifference || left.entityId.localeCompare(right.entityId);
      })[0] ?? null;
  }

  #rollbackMeal({ snake, wildlife, world }, originalError) {
    try {
      this.snakeState.restore(snake);
      this.wildlifeSystem.restore(wildlife);
      this.entityWorld.restore(world);
    } catch (rollbackError) {
      throw new AggregateError(
        [originalError, rollbackError],
        'Meal transaction failed and could not be fully rolled back',
      );
    }
    throw originalError;
  }

  strike({
    requestId = undefined,
    targetEntityId = undefined,
    tick = this.currentStep.tick,
    simulationTime = this.currentStep.simulationTime,
  } = {}) {
    const request = this.#prepareRequest(requestId);
    const previous = this.processedRequests.get(request.requestId);
    if (previous) return duplicateEvent(previous);
    const normalizedTick = normalizeTick(tick);
    const normalizedTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedTargetId = targetEntityId === undefined
      ? undefined
      : assertNonEmptyString(targetEntityId, 'targetEntityId');
    const record = (event, counters) => this.#record(
      event,
      counters,
      request.nextRequestNumber,
    );
    const snake = this.#snake();

    if (!snake) {
      return record(buildEvent({
        ok: false,
        type: 'strike',
        requestId: request.requestId,
        outcome: 'miss',
        reason: 'snake-unavailable',
        tick: normalizedTick,
        simulationTime: normalizedTime,
      }), { strikes: 1, misses: 1 });
    }
    if (normalizedTime < this.nextStrikeTime) {
      return record(buildEvent({
        ok: false,
        type: 'strike',
        requestId: request.requestId,
        outcome: 'cooldown',
        reason: 'strike-cooldown',
        tick: normalizedTick,
        simulationTime: normalizedTime,
        snakeEntityId: snake.entityId,
        targetEntityId: normalizedTargetId ?? null,
      }), { strikes: 1, cooldowns: 1 });
    }

    const target = this.#selectStrikeTarget(snake, normalizedTargetId);
    if (!target) {
      this.nextStrikeTime = normalizedTime + STRIKE_COOLDOWN_SECONDS;
      return record(buildEvent({
        ok: false,
        type: 'strike',
        requestId: request.requestId,
        outcome: 'miss',
        reason: 'no-valid-target',
        tick: normalizedTick,
        simulationTime: normalizedTime,
        snakeEntityId: snake.entityId,
        targetEntityId: normalizedTargetId ?? null,
      }), { strikes: 1, misses: 1 });
    }

    this.#assertEntityType(snake.entityId, 'player-snake');
    this.#assertEntityType(target.entityId, 'wildlife');
    const damage = strikeDamage(snake);
    const damaged = this.wildlifeSystem.damage(target.entityId, damage, {
      tick: normalizedTick,
      simulationTime: normalizedTime,
      sourceEntityId: snake.entityId,
    });
    this.nextStrikeTime = normalizedTime + STRIKE_COOLDOWN_SECONDS;
    const killed = damaged.lifecycle === 'corpse';
    return record(buildEvent({
      ok: true,
      type: 'strike',
      requestId: request.requestId,
      outcome: killed ? 'kill' : 'hit',
      tick: normalizedTick,
      simulationTime: normalizedTime,
      snakeEntityId: snake.entityId,
      targetEntityId: damaged.entityId,
      damage,
      targetHealth: damaged.health,
      targetLifecycle: damaged.lifecycle,
    }), {
      strikes: 1,
      hits: 1,
      ...(killed ? { kills: 1 } : {}),
    });
  }

  eat({
    requestId = undefined,
    targetEntityId = undefined,
    tick = this.currentStep.tick,
    simulationTime = this.currentStep.simulationTime,
  } = {}) {
    const request = this.#prepareRequest(requestId);
    const previous = this.processedRequests.get(request.requestId);
    if (previous) return duplicateEvent(previous);
    const normalizedTick = normalizeTick(tick);
    const normalizedTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedTargetId = targetEntityId === undefined
      ? undefined
      : assertNonEmptyString(targetEntityId, 'targetEntityId');
    const record = (event, counters) => this.#record(
      event,
      counters,
      request.nextRequestNumber,
    );
    const snake = this.#snake();

    if (!snake) {
      return record(buildEvent({
        ok: false,
        type: 'eat',
        requestId: request.requestId,
        outcome: 'miss',
        reason: 'snake-unavailable',
        tick: normalizedTick,
        simulationTime: normalizedTime,
      }), { misses: 1 });
    }
    if (normalizedTime < this.nextEatTime) {
      return record(buildEvent({
        ok: false,
        type: 'eat',
        requestId: request.requestId,
        outcome: 'cooldown',
        reason: 'eat-cooldown',
        tick: normalizedTick,
        simulationTime: normalizedTime,
        snakeEntityId: snake.entityId,
        targetEntityId: normalizedTargetId ?? null,
      }), { cooldowns: 1 });
    }

    const target = this.#selectEatTarget(snake, normalizedTargetId);
    if (!target) {
      this.nextEatTime = normalizedTime + EAT_COOLDOWN_SECONDS;
      return record(buildEvent({
        ok: false,
        type: 'eat',
        requestId: request.requestId,
        outcome: 'miss',
        reason: 'no-valid-corpse',
        tick: normalizedTick,
        simulationTime: normalizedTime,
        snakeEntityId: snake.entityId,
        targetEntityId: normalizedTargetId ?? null,
      }), { misses: 1 });
    }

    this.#assertEntityType(snake.entityId, 'player-snake');
    this.#assertEntityType(target.entityId, 'wildlife');
    const species = this.wildlifeSystem.getSpeciesDefinition(target.speciesId);
    const rewards = {
      nutrition: assertPositiveFinite(species.nutrition, 'species.nutrition'),
      length: assertPositiveFinite(species.growth.length, 'species.growth.length'),
      mass: assertPositiveFinite(species.growth.mass, 'species.growth.mass'),
      points: assertPositiveFinite(species.growth.points, 'species.growth.points'),
    };
    const backup = {
      snake: this.snakeState.snapshot(),
      wildlife: this.wildlifeSystem.snapshot(),
      world: this.entityWorld.snapshot(),
    };

    let snakeAfter;
    let consumed;
    try {
      this.snakeState.feed(rewards.nutrition);
      snakeAfter = this.snakeState.grow({
        lengthDelta: rewards.length,
        massDelta: rewards.mass,
        pointsDelta: rewards.points,
      });
      consumed = this.wildlifeSystem.consume(target.entityId);
    } catch (error) {
      return this.#rollbackMeal(backup, error);
    }
    this.nextEatTime = normalizedTime + EAT_COOLDOWN_SECONDS;

    return record(buildEvent({
      ok: true,
      type: 'eat',
      requestId: request.requestId,
      outcome: 'consumed',
      tick: normalizedTick,
      simulationTime: normalizedTime,
      snakeEntityId: snake.entityId,
      targetEntityId: consumed.entityId,
      targetHealth: 0,
      targetLifecycle: 'corpse',
      rewards,
      snakeAfter: {
        revision: snakeAfter.revision,
        hunger: snakeAfter.vitals.hunger,
        length: snakeAfter.length,
        mass: snakeAfter.mass,
        growthPoints: snakeAfter.growth.points,
      },
    }), { eats: 1 });
  }

  step({ stepSeconds, tick, simulationTime, input }) {
    const normalizedStepSeconds = assertPositiveFinite(stepSeconds, 'stepSeconds');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    if (!input || typeof input !== 'object') throw new TypeError('input must be an object');
    this.currentStep = {
      tick: normalizedTick,
      simulationTime: normalizedTime,
      stepSeconds: normalizedStepSeconds,
    };

    const strike = input.actions?.strike?.pressed
      ? this.strike({
        requestId: `tick-${normalizedTick}-strike`,
        tick: normalizedTick,
        simulationTime: normalizedTime,
      })
      : null;
    const eat = input.actions?.interact?.pressed
      ? this.eat({
        requestId: `tick-${normalizedTick}-eat`,
        tick: normalizedTick,
        simulationTime: normalizedTime,
      })
      : null;

    return deepFreeze({
      strike,
      eat,
      stateRef: this.getStateRef(),
    });
  }

  snapshot() {
    const payload = {
      version: HUNTING_SYSTEM_VERSION,
      revision: this.revision,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      nextRequestNumber: this.nextRequestNumber,
      nextStrikeTime: this.nextStrikeTime,
      nextEatTime: this.nextEatTime,
      counters: clone(this.counters),
      processedRequests: [...this.processedRequests.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([requestId, event]) => ({ requestId, event: clone(event) })),
      lastEvent: clone(this.lastEvent),
    };
    return deepFreeze({
      ...payload,
      checksum: computeHuntingChecksum(payload),
    });
  }

  exportSnapshot() {
    return JSON.stringify(this.snapshot());
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== HUNTING_SYSTEM_VERSION) {
      throw new RangeError(`Unsupported hunting snapshot version: ${parsed.version}`);
    }
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const tick = normalizeTick(parsed.tick);
    const simulationTime = assertFiniteNonNegative(parsed.simulationTime, 'snapshot.simulationTime');
    const stepSeconds = parsed.stepSeconds === null
      ? null
      : assertPositiveFinite(parsed.stepSeconds, 'snapshot.stepSeconds');
    const nextRequestNumber = assertNonNegativeInteger(
      parsed.nextRequestNumber,
      'snapshot.nextRequestNumber',
    );
    if (nextRequestNumber < 1) throw new RangeError('snapshot.nextRequestNumber must be at least 1');
    const nextStrikeTime = assertFiniteNonNegative(parsed.nextStrikeTime, 'snapshot.nextStrikeTime');
    const nextEatTime = assertFiniteNonNegative(parsed.nextEatTime, 'snapshot.nextEatTime');
    const counters = normalizeCounters(parsed.counters);
    if (!Array.isArray(parsed.processedRequests)) {
      throw new TypeError('snapshot.processedRequests must be an array');
    }
    const processedRequests = new Map();
    for (const entry of parsed.processedRequests) {
      assertPlainObject(entry, 'processed request');
      const requestId = assertNonEmptyString(entry.requestId, 'processed request ID');
      if (processedRequests.has(requestId)) {
        throw new RangeError('snapshot contains duplicate request IDs');
      }
      const event = normalizeEvent(entry.event);
      if (event.requestId !== requestId) {
        throw new RangeError('processed request event ID must match its key');
      }
      processedRequests.set(requestId, event);
    }
    if (processedRequests.size !== revision) {
      throw new RangeError('processed request count must equal snapshot.revision');
    }
    const lastEvent = parsed.lastEvent === null ? null : normalizeEvent(parsed.lastEvent);
    if (lastEvent && !processedRequests.has(lastEvent.requestId)) {
      throw new RangeError('lastEvent must reference a processed request');
    }
    const derivedCounters = deriveCounters(processedRequests.values());
    if (JSON.stringify(derivedCounters) !== JSON.stringify(counters)) {
      throw new RangeError('snapshot counters must match processed requests');
    }

    const payload = {
      version: HUNTING_SYSTEM_VERSION,
      revision,
      tick,
      simulationTime,
      stepSeconds,
      nextRequestNumber,
      nextStrikeTime,
      nextEatTime,
      counters: clone(counters),
      processedRequests: [...processedRequests.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([requestId, event]) => ({ requestId, event: clone(event) })),
      lastEvent: clone(lastEvent),
    };
    if (typeof parsed.checksum !== 'string' || parsed.checksum !== computeHuntingChecksum(payload)) {
      throw new RangeError('snapshot checksum is invalid');
    }

    this.revision = revision;
    this.currentStep = { tick, simulationTime, stepSeconds };
    this.nextRequestNumber = nextRequestNumber;
    this.nextStrikeTime = nextStrikeTime;
    this.nextEatTime = nextEatTime;
    this.counters = counters;
    this.processedRequests = processedRequests;
    this.lastEvent = lastEvent;
    return this.snapshot();
  }

  reset() {
    this.revision = 0;
    this.nextRequestNumber = 1;
    this.nextStrikeTime = 0;
    this.nextEatTime = 0;
    this.processedRequests = new Map();
    this.lastEvent = null;
    this.counters = normalizeCounters();
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
    return this.getStatus();
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      tick: snapshot.tick,
      processedRequestCount: snapshot.processedRequests.length,
      lastEventType: snapshot.lastEvent?.type ?? null,
      lastOutcome: snapshot.lastEvent?.outcome ?? null,
      checksum: snapshot.checksum,
    });
  }

  getStatus() {
    return deepFreeze({
      version: HUNTING_SYSTEM_VERSION,
      revision: this.revision,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      nextRequestNumber: this.nextRequestNumber,
      nextStrikeTime: this.nextStrikeTime,
      nextEatTime: this.nextEatTime,
      processedRequestCount: this.processedRequests.size,
      counters: clone(this.counters),
      lastEvent: clone(this.lastEvent),
    });
  }
}
